import React, { Component, Fragment } from 'react';
import scrollToElement from 'scroll-to-element';

import I18n from '~/utils/translations';

import Header from '~/components/agent_form/header';
import CalculateChargeButton, { ChargeTypes } from '~/components/agent_form/calculate_charge_button';
import HttpRequestor from '~/components/http_requestor';
import PropertiesDropdown from '~/components/agent_form/properties_dropdown';

import Button from '~/components/shared/button';
import Counter from '~/components/shared/counter';
import DatePicker from '~/components/shared/date_picker';
import DropDown from '~/components/shared/drop_down';
import Input from '~/components/shared/input_field';
import TickGreen from '~/images/tick-green.svg';

import { earliestActualMoveInDate } from '~/utils/move_in_date';
import { validEmail, validPrice, companyEmailValidation } from '~/utils/validators';
import { errorsCount } from '~/utils/errors';
import AppendableCharge from '~/components/agent_form/appendable_charge';
import LabelWithTooltip from '~/components/agent_form/label_with_tooltip';
import RentChargeInput from '~/components/agent_form/rent_charge_input';

class AgentForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedProperty: null,
      price: '',
      holdingDepositAmount: '',
      branch: '',
      agentEmail: '',
      firstName: '',
      lastName: '',
      email: '',
      adults: 1,
      children: 0,
      leaseOption: '',
      holdingDepositCollection: '',
      moveInDate: '',
      prePaperworkRequests: '',
      portfolioCharges: [],
      charges: [],
      errors: {
        property: [],
        price: [],
        holdingDepositAmount: [],
        branch: [],
        agentEmail: [],
        firstName: [],
        lastName: [],
        email: [],
        leaseOptions: [],
        holdingDepositCollection: [],
        moveInDate: [],
        charges: [],
      },
      submitting: false,
      success: false,
      error: false,
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);

    if (this.props.property) {
      this.fetchCharges(this.props.property.attributes.portfolio_id);
    }
    this.setState({
      selectedProperty: this.props.property,
      firstName: params.get('first_name') || '',
      lastName: params.get('last_name') || '',
      email: params.get('email') || '',
      moveInDate: params.get('move_in_date') || '',
    });
  }

  render() {
    return (
      <Fragment>
        <Header logo={this.props.cobrandingLogo} />

        <div className="agent-form">
          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_1.text.address')}</p>

          <PropertiesDropdown
            portfolios={this.props.portfolios}
            selectedProperty={this.state.selectedProperty}
            onPropertySelect={this.onPropertySelect}
            clearSelection={this.clearSelection}
            errors={this.state.errors.property}
          />

          <Fragment>
            <LabelWithTooltip
              label={I18n.t('pages.agents_form.charges.tooltip_label')}
              tooltip={I18n.t('pages.agents_form.charges.tooltip')}
            />

            <RentChargeInput
              value={this.state.price}
              onChange={this.onInputChange}
              onBlur={this.onPriceBlur}
              errors={this.state.errors.price}
            />

            {this.showAdditionalCharges && (
              <AppendableCharge
                inputValues={this.state.charges}
                appendText={I18n.t('pages.agents_form.charges.add_charge_btn')}
                onChange={this.onChargesChange}
                portfolioCharges={this.state.portfolioCharges}
                hint={I18n.t('pages.agents_form.charges.hint')}
                errors={this.state.errors.charges}
              />
            )}
          </Fragment>

          {this.showHoldingDepositAmountOption && (
            <Fragment>
              <p className="agent-form__step-sub-title">
                {I18n.t('pages.agents_form.step_1.text.holding_deposit_amount')}
              </p>

              <Input
                inputRef={(ref) => (this.holdingDepositAmountInput = ref)}
                name="holdingDepositAmount"
                type="text"
                animatedPlaceholder={I18n.t('pages.agents_form.step_1.input_placeholders.holding_deposit_amount')}
                prepend={I18n.t('currency.symbol')}
                value={this.state.holdingDepositAmount}
                onChange={this.onInputChange}
                errors={this.state.errors.holdingDepositAmount}
                onBlur={this.onPriceBlur}
                append={
                  this.props.enableChargeCalculationButton && (
                    <CalculateChargeButton
                      inputName="holdingDepositAmount"
                      inputRef={this.holdingDepositAmountInput}
                      propertyId={this.state.selectedProperty.id}
                      rentAmount={this.state.price && parseFloat(this.state.price) * 100}
                      moveInDate={this.state.moveInDate}
                      calculatedValue={ChargeTypes.holdingDepositAmount}
                      onCalculated={this.onCalculated}
                    />
                  )
                }
              />
            </Fragment>
          )}

          {this.showHoldingDepositCollectionOption && (
            <Fragment>
              <p className="agent-form__step-sub-title">
                {I18n.t('pages.agents_form.step_1.holding_deposit_collection_body')}
              </p>

              <DropDown
                placeholder={I18n.t('pages.agents_form.step_1.holding_deposit_collection_body_placeholder')}
                items={this.holdingDepositCollectionOptions}
                onChange={this.onChangeHoldingDepositCollectionOption}
                errors={this.state.errors.holdingDepositCollection}
                name={'holding_deposit_collection'}
              />
            </Fragment>
          )}

          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_2.text')}</p>

          <Input
            name="branch"
            animatedPlaceholder={I18n.t('pages.agents_form.step_2.input_placeholder')}
            value={this.state.branch}
            onChange={this.onInputChange}
            errors={this.state.errors.branch}
          />

          <div className="agent-form__section">
            <LabelWithTooltip
              label={I18n.t('pages.agents_form.step_2.email_text')}
              tooltip={I18n.t('pages.agents_form.step_2.email_tooltip')}
            />
          </div>
          <Input
            name="agentEmail"
            animatedPlaceholder={I18n.t('pages.agents_form.step_2.email_input_placeholder')}
            value={this.state.agentEmail}
            onChange={this.onInputChange}
            errors={this.state.errors.agentEmail}
          />
          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_3.text')}</p>

          <Input
            name="firstName"
            animatedPlaceholder={I18n.t('pages.agents_form.step_3.input_placeholders.first_name')}
            value={this.state.firstName}
            onChange={this.onInputChange}
            errors={this.state.errors.firstName}
          />

          <Input
            name="lastName"
            animatedPlaceholder={I18n.t('pages.agents_form.step_3.input_placeholders.last_name')}
            value={this.state.lastName}
            onChange={this.onInputChange}
            errors={this.state.errors.lastName}
          />

          <Input
            name="email"
            type="email"
            animatedPlaceholder={I18n.t('pages.agents_form.step_3.input_placeholders.email')}
            value={this.state.email}
            onChange={this.onInputChange}
            errors={this.state.errors.email}
          />

          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_4.text.number_of_residents')}</p>

          <div className="agent-form__inline-section-container">
            <div className="agent-form__inline-section">
              <Counter
                label={I18n.t('pages.agents_form.step_4.text.adults')}
                quantity={this.state.adults}
                decreaseQuantity={() => this.onChangeResidentCount(-1, 1, 'adults')}
                increaseQuantity={() => this.onChangeResidentCount(1, 1, 'adults')}
              />
            </div>

            <div className="agent-form__inline-section">
              <Counter
                label={I18n.t('pages.agents_form.step_4.text.children')}
                quantity={this.state.children}
                decreaseQuantity={() => this.onChangeResidentCount(-1, 0, 'children')}
                increaseQuantity={() => this.onChangeResidentCount(1, 0, 'children')}
              />
            </div>
          </div>

          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_4.text.lease_options')}</p>

          <DropDown
            placeholder={I18n.t('pages.agents_form.step_4.lease_options_placeholder')}
            items={this.leaseOptions}
            onChange={this.onChangeLeaseOption}
            errors={this.state.errors.leaseOptions}
            name={'lease_options'}
          />

          <p className="agent-form__step-sub-title">{I18n.t('pages.agents_form.step_4.text.move_in_date')}</p>

          <DatePicker
            dateFormat="YYYY-MM-DD"
            availableDate={this.earliestMoveInDate}
            holidays={this.props.observedHolidays}
            onChange={this.onChangeMoveInDate}
            isLondon={true}
            errors={this.state.errors.moveInDate}
            selectedDay={this.state.moveInDate ? new Date(this.state.moveInDate) : undefined}
          />

          <Input
            type="textarea"
            name="prePaperworkRequests"
            label={I18n.t('pages.agents_form.step_4.text.pre_paperwork_requests')}
            value={this.state.prePaperworkRequests}
            onChange={this.onInputChange}
            rows="3"
            tabIndex="1"
            version="2"
          />

          <div className="agent-form__section">
            {!this.state.success && (
              <Fragment>
                <p className="agent-form__step-text agent-form__step-text--centered">
                  {I18n.t('pages.agents_form.info_text')}
                </p>

                <Button
                  buttonClass="agent-form__button"
                  text={I18n.t('pages.agents_form.button_text')}
                  onClickHandler={this.onSubmit}
                  isTransitioning={this.state.submitting}
                  disabled={this.state.success}
                />
              </Fragment>
            )}

            {this.state.success && (
              <Fragment>
                <div className="agent-form__confirmation-message">
                  <p className="agent-form__submitted">
                    {I18n.t('pages.agents_form.button_text_submitted')}
                    <img src={TickGreen} className="agent-form__tick" />
                  </p>
                  {I18n.t('pages.agents_form.success').map((text, i) => {
                    return (
                      <p key={i} className="agent-form__submitted-text" dangerouslySetInnerHTML={{ __html: text }}></p>
                    );
                  })}
                </div>

                <a className="agent-form__refresh-text" onClick={() => location.reload()}>
                  {I18n.t('pages.agents_form.refresh')}
                </a>
              </Fragment>
            )}

            {this.state.error && (
              <div className="agent-form__confirmation-message agent-form__confirmation-message--validation">
                {I18n.t('pages.agents_form.error', { email: I18n.t('brand.email') })}
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  }

  onInputChange = (evt) => {
    this.setState({ [evt.target.name]: evt.target.value });
  };

  onCalculated = (newValue, inputRef) => {
    this.setState(newValue, () => inputRef && inputRef.focus());
  };

  onPriceBlur = (evt) => {
    let price = evt.target.value;
    if (price !== '' && !isNaN(price)) price = Number(evt.target.value).toFixed(2);
    this.setState({ [evt.target.name]: price });
  };

  onChangeResidentCount = (delta, min, stateKey) => {
    this.setState({ [stateKey]: Math.max(min, this.state[stateKey] + delta) });
  };

  onChangeLeaseOption = (evt) => {
    this.setState({ leaseOption: evt.target.value || null });
  };

  onChangeHoldingDepositCollectionOption = (evt) => {
    this.setState({ holdingDepositCollection: evt.target.value || '' });
  };

  onChangeMoveInDate = (moveInDate) => {
    this.setState({ moveInDate });
  };

  onChargesChange = (updatedCharges) => {
    this.setState({ charges: updatedCharges });
  };

  onSubmit = () => {
    this.setState({ submitting: true });

    if (this.validate()) {
      this.submitLeadDetails(this.formParams);
    } else {
      this.setState({ submitting: false });
    }
  };

  submitLeadDetails = async (params) => {
    this.setState({ submitting: true }, async () => {
      const requestor = new HttpRequestor();

      try {
        const resp = await requestor.call(this.props.submitUrl, 'POST', params);
        this.setState({ success: true, error: false });
      } catch (err) {
        console.error('Error submitting lead details', err);
        this.setState({ success: false, error: true });
      } finally {
        this.setState({ submitting: false });
      }
    });
  };

  onPropertySelect = (selectedProperty) => {
    this.setState({
      selectedProperty,
      moveInDate: '',
    });

    const portfolioId = selectedProperty.attributes.portfolio_id;

    this.fetchCharges(portfolioId);
  };

  fetchCharges = (portfolioId) => {
    const requestor = new HttpRequestor();
    const url = new URL('/charges', window.location.origin);
    url.searchParams.append('portfolio_id', portfolioId);

    requestor.call(url, 'GET').then(this._chargesSuccess).catch(this._chargesError);
  };

  _chargesSuccess = (response) => {
    const filteredCharges = response.items.filter((charge) => !charge.attributes.part_of_initial_payment);

    this.setState({
      portfolioCharges: filteredCharges,
    });
  };

  _chargesError = () => {
    this.setState({
      portfolioCharges: [],
    });
  };

  clearSelection = () => {
    this.setState({
      selectedProperty: null,
    });
  };

  validate = () => {
    const holdingDepositAmountValidation =
      !this.showHoldingDepositAmountOption ||
      (validPrice(this.state.holdingDepositAmount) && this.state.holdingDepositAmount >= 1);

    const additionalChargeErrors = this.validateAdditionalCharges();

    let errors = {
      property: this.state.selectedProperty ? [] : [I18n.t('pages.agents_form.errors.property')],
      branch: this.state.branch ? [] : [I18n.t('pages.agents_form.errors.branch')],
      agentEmail: companyEmailValidation(this.state.agentEmail),
      price: validPrice(this.state.price) ? [] : [I18n.t('pages.agents_form.errors.charge_amount')],
      holdingDepositAmount: holdingDepositAmountValidation
        ? []
        : [I18n.t('pages.agents_form.errors.holding_deposit_amount')],
      firstName: this.state.firstName ? [] : [I18n.t('pages.agents_form.errors.first_name')],
      lastName: this.state.lastName ? [] : [I18n.t('pages.agents_form.errors.last_name')],
      email: validEmail(this.state.email) ? [] : [I18n.t('pages.agents_form.errors.email')],
      holdingDepositCollection:
        this.state.holdingDepositCollection || !this.showHoldingDepositCollectionOption
          ? []
          : [I18n.t('pages.agents_form.errors.holding_deposit')],
      moveInDate: this.state.moveInDate ? [] : [I18n.t('pages.agents_form.errors.move_in_date')],
      charges: additionalChargeErrors,
      leaseOptions: this.state.leaseOption ? [] : [I18n.t('pages.agents_form.errors.lease_options')],
      moveInDate: this.state.moveInDate ? [] : [I18n.t('pages.agents_form.errors.move_in_date')],
    };

    this.setState({ errors });

    const flattenedErrors = {
      ...errors,
      charges: errors.charges.flatMap((charge) => Object.values(charge).flat()),
    };

    const noErrors = errorsCount(flattenedErrors) === 0;

    if (!noErrors) {
      // The -100 offset is because the validation error is below the input, so adjust to view the input.
      setTimeout(() => scrollToElement('.validation-error', { offset: -100, duration: 250 }, 500));
    }

    return noErrors;
  };

  validateAdditionalCharges = () => {
    return this.state.charges.map((charge) => ({
      description: charge.description ? [] : [I18n.t('pages.agents_form.errors.charge_description')],
      amount: validPrice(charge.amount) ? [] : [I18n.t('pages.agents_form.errors.charge_amount')],
    }));
  };

  get showHoldingDepositCollectionOption() {
    return (
      this.state.selectedProperty &&
      this.state.selectedProperty.attributes.agent_chooses_who_collects_holding_deposit_enabled
    );
  }

  get holdingDepositPaymentMethod() {
    const stripeEnabled = this.state.selectedProperty?.attributes?.stripe_payments_enabled;
    if (!stripeEnabled) {
      return 'collected_by_partner';
    }
    return this.state.holdingDepositCollection;
  }

  get showHoldingDepositAmountOption() {
    return (
      this.state.selectedProperty && this.state.selectedProperty.attributes.agent_can_set_holding_deposit_amount_enabled
    );
  }

  get showAdditionalCharges() {
    return (
      this.state.selectedProperty &&
      this.state.selectedProperty.attributes.additional_charges_enabled &&
      this.state.portfolioCharges.length > 0
    );
  }

  get earliestMoveInDate() {
    if (!this.state.selectedProperty) {
      return null;
    }

    const propertyEmptyFromDate = this.state.selectedProperty.attributes.available_date;
    const availableFrom = this.state.selectedProperty.attributes.available_from;

    return earliestActualMoveInDate(propertyEmptyFromDate, availableFrom);
  }

  get formParams() {
    return {
      property_id: this.state.selectedProperty.id,
      price_in_pence: parseFloat(this.state.price) * 100,
      holding_deposit_in_pence: parseFloat(this.state.holdingDepositAmount) * 100,
      branch: this.state.branch.trim(),
      agent_email: this.state.agentEmail.trim(),
      first_name: this.state.firstName.trim(),
      last_name: this.state.lastName.trim(),
      email: this.state.email.trim(),
      pre_paperwork_adults_count: this.state.adults,
      pre_paperwork_children_count: this.state.children,
      pre_paperwork_lease_option: this.state.leaseOption,
      move_in_date: this.state.moveInDate,
      pre_paperwork_requests: this.state.prePaperworkRequests.trim(),
      holding_deposit_payment_method: this.holdingDepositPaymentMethod,
      charges: this.state.charges.map((charge) => ({
        description: charge.description,
        amount: parseFloat(charge.amount) * 100,
        frequency: 'P1M', // monthly
        part_of_initial_payment: false,
        part_of_referencing: charge.part_of_referencing,
      })),
    };
  }

  get leaseOptions() {
    const { selectedProperty } = this.state;
    const formattedLeaseOptions = [];

    if (!selectedProperty) {
      return formattedLeaseOptions;
    }

    for (const option in selectedProperty.attributes.lease_options) {
      const value = option;
      const label = selectedProperty.attributes.lease_options[option].label;
      formattedLeaseOptions.push({ value, label });
    }

    return formattedLeaseOptions;
  }

  get holdingDepositCollectionOptions() {
    // pass this down from foundations HoldingDepositSettingsService?
    // hard coded for now
    return [
      { value: 'stripe', label: 'Yes' },
      { value: 'collected_by_partner', label: 'No' },
    ];
  }
}

export default AgentForm;
